/**
 * The game's location plan in the admin's round panel: where the admin intends
 * to play, editable at any time, plus how many players already have the maps.
 */

import { useState } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  type Game,
  type GameLocation,
  type HnsUpdateLocationPlanRequest,
  type HnsUpdateLocationPlanResponse,
} from '@swtorbit/shared-socket-events';
import { useAssetStatus } from '../../hooks/useAssetStatus';
import { HnsLocationPlanEditor } from './HnsLocationPlanEditor';

export function HnsLocationPlanPanel({ game }: { game: Game }) {
  const { socket } = useSocketContext();
  const assets = useAssetStatus(game.roomCode);
  const plan = game.hnsLocationPlan ?? [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<GameLocation[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEditing = () => {
    setDraft(plan.map(({ logAreaId, zoneMapId }) => ({ logAreaId, zoneMapId })));
    setError(null);
    setEditing(true);
  };

  const save = () => {
    if (!socket) return;
    setSaving(true);
    socket.emit(
      SocketEvent.HNS_UPDATE_LOCATION_PLAN,
      { roomCode: game.roomCode, locations: draft } satisfies HnsUpdateLocationPlanRequest,
      (res: HnsUpdateLocationPlanResponse) => {
        setSaving(false);
        if (res?.success) setEditing(false);
        else setError(res?.error ?? 'Failed to save the location plan');
      },
    );
  };

  const current = assets?.players.filter((p) => p.version === assets.version) ?? [];
  const ready = current.filter((p) => p.ready + p.failed >= p.total).length;

  return (
    <div className="card p-sm mb-sm">
      <div className="d-flex justify-content-between align-items-center gap-xs">
        <h4 className="m-0">📍 Location plan</h4>
        {!editing && (
          <button type="button" className="btn btn--outline btn--slate" onClick={startEditing}>
            {plan.length ? 'Edit' : 'Plan locations'}
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-xs d-flex flex-column gap-xs">
          <HnsLocationPlanEditor value={draft} onChange={setDraft} disabled={saving} />
          {error && <div className="field__error">{error}</div>}
          <div className="d-flex gap-xs">
            <button type="button" className="btn" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save plan'}
            </button>
            <button type="button" className="btn btn--outline btn--slate" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      ) : plan.length ? (
        <ul className="mt-xs mb-0">
          {plan.map((location) => (
            <li key={`${location.logAreaId}-${location.zoneMapId}`} className="fs-sm">
              {location.zone?.name ? `${location.areaName} · ${location.zone.name}` : location.areaName}
            </li>
          ))}
        </ul>
      ) : (
        <p className="fs-sm text-slate mt-xs mb-0">
          No locations planned. Rounds can still pick one, or find it from where the hiders are.
        </p>
      )}

      {assets && assets.players.length > 0 && (
        <p className="fs-sm text-slate mt-xs mb-0">
          Maps downloaded: {ready}/{assets.players.length} players
        </p>
      )}
    </div>
  );
}
